/**
 * Timezone configuration for the application
 * All times should be handled in Central Time (America/Chicago)
 */

import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

export const BUSINESS_TIMEZONE = 'America/Chicago';
export const TIMEZONE_LABEL = 'CT';
export const TIMEZONE_FULL_LABEL = 'Central Time';
export const MINIMUM_BOOKING_LEAD_TIME_MINUTES = 30; // Clients need 30 min to book/prepare

/**
 * Converts any Date/ISO string to its Central Time representation for display.
 * Use this whenever you need to show a time in CT.
 */
export function toCTDate(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  return toZonedTime(d, BUSINESS_TIMEZONE);
}

/**
 * Creates a proper UTC ISO string from Central Time date/time components.
 * E.g. createCTTimestamp('2026-01-21', 15, 0) means "3:00 PM Central"
 * and returns the correct UTC instant.
 */
export function createCTTimestamp(dateStr: string, hours: number, minutes: number): string {
  // Build a Date object that represents this date/time AS IF it were in CT,
  // then fromZonedTime converts it to the correct UTC instant.
  const [year, month, day] = dateStr.split('-').map(Number);
  const ctDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const utcDate = fromZonedTime(ctDate, BUSINESS_TIMEZONE);
  return utcDate.toISOString();
}

/**
 * Formats a UTC date/ISO string as a CT time string (e.g. "3:00 PM").
 */
export function formatCTTime(date: Date | string, fmt: string = 'h:mm a'): string {
  const ctDate = toCTDate(date);
  return format(ctDate, fmt);
}

/**
 * Formats a UTC date/ISO string as a CT date string (e.g. "2026-01-21").
 */
export function formatCTDate(date: Date | string, fmt: string = 'yyyy-MM-dd'): string {
  const ctDate = toCTDate(date);
  return format(ctDate, fmt);
}
